import type { BoxProps } from '@chakra-ui/react'
import { Skeleton, Stack, Text } from '@chakra-ui/react'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'

import { formatNumber } from '@/utils/formatNumber'

export interface StatChangeValueProps extends Omit<BoxProps, 'children'> {
  value?: number
  baselineValue?: number
  baselineValueLabel?: string
  size?: 'md' | 'sm'
  relative?: boolean
  relativeUnit?: string
  absoluteUnit?: string
  maxFractionDigits?: number
  isLoading?: boolean
}

export const StatChangeValue = ({
  baselineValue,
  baselineValueLabel,
  value,
  size = 'md',
  relative = true,
  relativeUnit = '%',
  absoluteUnit = 'Kč',
  maxFractionDigits = 1,
  isLoading = false,
  ...props
}: StatChangeValueProps) => {
  if (baselineValue === undefined || baselineValue === null || value === undefined || value === null)
    return null

  const change = value - baselineValue
  const absChange = Math.abs(change)
  const percentageChange = (change / baselineValue) * 100
  const absPercentageChange = Math.abs(percentageChange)

  const isPositive = change > 0

  const displayValue = relative ? absPercentageChange : absChange
  const displayUnit = relative ? relativeUnit : absoluteUnit

  if (!isLoading && !change)
    return null

  const closeToZeroChange = displayValue > -0.05 && displayValue < 0.05

  return <Skeleton isLoaded={!isLoading} h={5} {...props}>
    <CustomTooltip boxProps={{ as: 'p', display: 'inline-block' }} label={<>
      <Stack gap={0}>
        {relative && <Text fontSize={size} fontWeight="medium" color={isPositive ? 'green.600' : 'red.600'}>
          {isPositive ? '+' : '-'} {formatNumber(absChange, { maximumFractionDigits: maxFractionDigits })} {absoluteUnit}
          &nbsp;
          ({formatNumber(absPercentageChange, { maximumFractionDigits: maxFractionDigits })} {relativeUnit})
        </Text>}
        <Text color='gray.700' fontSize={size === 'sm' ? 'xs' : 'sm'}>změna oproti období {baselineValueLabel}</Text>
      </Stack>
    </>} placement="bottom-start" hasArrow>
      {closeToZeroChange ?
        <Text as='span' color="gray.500" fontSize={size} fontWeight="medium" {...props}>&plusmn; 0 {displayUnit}</Text>
        :
        <Text as='span' color={isPositive ? 'green.600' : 'red.600'} fontSize={size} fontWeight="medium">
          {isPositive ? '+' : '-'} {formatNumber(displayValue, { maximumFractionDigits: maxFractionDigits })} {displayUnit}
        </Text>
      }
    </CustomTooltip>
  </Skeleton>
}